import { Button, useTheme } from '@itsrever/design-system'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'
import { GithubComItsreverDomainModelsPkgModelsAddress as ModelsAddress } from '@itsrever/returns-api-types'
import styled from 'styled-components'
import { device, disabledForm } from '@/utils'
import AddressForm from '@/components/AddressForm'
import TopBar from '@/components/TopBarComponent/TopBar'
import { setReturnAddress, RefundActions } from '@/redux/return/returnSlice'
import { ReverCard } from '@/components/ReverCard'
import {
    useReturnMethods,
    useTranslate,
    usePages,
    useProvinces,
    useRefundMethods
} from '@/hooks'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { LoadingModal } from '@/components/Loading'
import { PbECommerceSettingsUseCaseEnum } from '@itsrever/rever-api'
import HoffTopBar from '@/components/HoffTopBar/hoff-top-bar'
import clsx from 'clsx'
import { addToast } from '@heroui/react'

function ReturnAddress() {
    const { pages } = usePages()
    const { t } = useTranslation()
    const { translate } = useTranslate()
    const navigate = useNavigate()
    const theme = useTheme()
    const dispatch = useAppDispatch()

    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )
    const isReverLite =
        settings.use_case === PbECommerceSettingsUseCaseEnum.Lite

    const address = useAppSelector((store) => store.return.returnAddress)
    const setAddress = (address: ModelsAddress) => {
        dispatch(setReturnAddress(address))
    }

    const refundMethods = useAppSelector(
        (store) =>
            store.refundMethodsApi.refundMethods.response.result?.refund_methods
    )
    const provisionalRefundAmount = useAppSelector(
        (store) => store.refundMethodsApi.provisionalRefundAmount
    )
    const isKeepYourItemOffered = refundMethods?.some(
        (method) => method.keep_your_item?.enabled === true
    )

    const { provinces } = useProvinces(
        address.country_code,
        address.state_province_code
    )
    const { callReturnMethods, loadingReturnMethods } = useReturnMethods(
        onSuccessReturnMethods
    )
    const { callRefundMethods, loadingRefundMethods } = useRefundMethods(
        onSuccessRefundMethods
    )

    const order = useAppSelector(
        (store) => store.returnsApi.order?.response?.order
    )
    const returnedItems = useAppSelector((store) => store.return.returnedItems)
    const onlyExchanges = returnedItems.every(
        (item) => item.item.action === RefundActions.ToExchange
    )

    useEffect(() => {
        if ((!order || !Object.keys(order)) && !isReverLite) {
            addToast({
                description: t('toast_errors.lost_order')
            })
            navigate(pages.Landing)
        }
    }, [])

    function onSuccessRefundMethods() {
        if (
            (onlyExchanges && !isKeepYourItemOffered) ||
            (provisionalRefundAmount &&
                provisionalRefundAmount <= 0 &&
                !isKeepYourItemOffered)
        ) {
            callReturnMethods(address)
        } else {
            navigate(pages.RefundMethod)
        }
    }
    function onSuccessReturnMethods() {
        navigate(pages.ReturnMethod)
    }

    const onClickContinue = () => {
        callRefundMethods(address)
    }

    const logoUrl = settings.branding?.logo_url
    const title = settings.branding?.title
    const backgroundUrl = settings.branding?.background_image_url

    const isMisako = settings.ecommerce_group === 'misako'
    const isHoff = settings.ecommerce_group === 'hoff'

    if (isHoff) {
        return (
            <>
                {loadingRefundMethods || loadingReturnMethods ? (
                    <LoadingModal loading />
                ) : null}
                <TopBar page="return-address" />
                <div className="relative mx-auto flex size-full max-w-[1000px] grow flex-col items-center bg-cover bg-center bg-no-repeat px-4 md:px-10">
                    <HoffTopBar />
                    <div className="size-full">
                        <div className="mb-20 mt-10 w-full max-w-screen-lg">
                            <div
                                className="my-10 font-haas font-light"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'return_address_return_info'
                                    )
                                }}
                            />
                            <AddressForm
                                addressObject={address}
                                setAddressObject={setAddress}
                            />
                            <div className="mt-6 flex w-full">
                                <button
                                    onClick={onClickContinue}
                                    id="returnAddressContinue"
                                    disabled={disabledForm(
                                        address,
                                        provinces ? provinces.length : 0,
                                        undefined
                                    )}
                                    className="ml-auto mt-8 w-fit min-w-44 cursor-pointer rounded-xl bg-black p-4 font-haas text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-500"
                                >
                                    {translate('return_page_button_continue')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    if (isMisako) {
        return (
            <>
                {loadingRefundMethods || loadingReturnMethods ? (
                    <LoadingModal loading />
                ) : null}
                <div className="relative grid grid-cols-1 md:grid-cols-2">
                    <div
                        className={clsx(
                            'sticky top-0 -z-10 h-screen w-full [grid-area:1/1] md:z-0 md:[grid-area:1/2]',
                            {
                                'bg-neutral-200': !backgroundUrl
                            }
                        )}
                    >
                        {backgroundUrl ? (
                            <img
                                src={backgroundUrl}
                                className="size-full object-cover"
                                alt=""
                            />
                        ) : null}
                    </div>
                    <div className="flex h-full flex-col gap-4 bg-white p-4 [grid-area:1/1] md:[grid-area:1/1] lg:px-10 lg:py-6">
                        <div className="flex w-full items-end">
                            {logoUrl ? (
                                <div className="mr-auto">
                                    <img
                                        style={
                                            {
                                                '--logo-desktop-height':
                                                    '1.5rem',
                                                '--logo-mobile-height': '1.2rem'
                                            } as React.CSSProperties
                                        }
                                        width={285}
                                        height={70}
                                        src={logoUrl}
                                        alt={title}
                                        className="h-[var(--logo-mobile-height)] w-auto object-contain lg:h-[var(--logo-desktop-height)]"
                                    />
                                </div>
                            ) : null}
                            <div className="flex items-center gap-5">
                                <a
                                    href="https://www.misako.com/pages/wishlist"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src="https://www.misako.com/cdn/shop/files/favorite.svg?v=1731587455"
                                        width={20}
                                        height={20}
                                    />
                                </a>
                                <a
                                    href="https://www.misako.com/account"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="20"
                                        height="21"
                                        viewBox="0 0 20 21"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M19 20V17.7778C19 15.3222 16.9863 13.3333 14.5 13.3333H5.5C3.01375 13.3333 1 15.3222 1 17.7778V20"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                        <path
                                            d="M10 9C12.2091 9 14 7.20914 14 5C14 2.79086 12.2091 1 10 1C7.79086 1 6 2.79086 6 5C6 7.20914 7.79086 9 10 9Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                                <a
                                    href="https://www.misako.com/collections/top-ventas"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <svg
                                        width="19"
                                        height="19"
                                        viewBox="0 0 19 19"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M13.6969 13.9469L18 18.25M15.875 8.6875C15.875 12.7951 12.5451 16.125 8.4375 16.125C4.32988 16.125 1 12.7951 1 8.6875C1 4.57988 4.32988 1.25 8.4375 1.25C12.5451 1.25 15.875 4.57988 15.875 8.6875Z"
                                            stroke="rgb(21, 21, 21)"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        ></path>
                                    </svg>
                                </a>
                            </div>
                        </div>
                        <div
                            color="#ffffff"
                            className="flex w-full flex-1 flex-col"
                        >
                            <h1 className="mt-5 text-lg font-[300] tracking-wider text-neutral-600 [font-family:Inter] lg:text-xl">
                                Dirección
                            </h1>
                            <p
                                className="mb-9 mt-2 text-[15px] font-[300] tracking-wide [font-family:Inter]"
                                dangerouslySetInnerHTML={{
                                    __html: translate(
                                        'return_address_return_info'
                                    )
                                }}
                            />
                            <AddressForm
                                addressObject={address}
                                setAddressObject={setAddress}
                            />
                            <div className="mt-auto flex items-center justify-between gap-4">
                                <div className="w-full md:max-w-[180px]">
                                    <Button
                                        onClick={() => navigate(-1)}
                                        id="returnAddressContinue"
                                        fullWidth
                                        variant="outlined"
                                    >
                                        <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                                            Volver
                                        </span>
                                    </Button>
                                </div>
                                <div className="w-full md:max-w-[180px]">
                                    <Button
                                        onClick={onClickContinue}
                                        id="returnAddressContinue"
                                        fullWidth
                                        disabled={disabledForm(
                                            address,
                                            provinces ? provinces.length : 0,
                                            undefined
                                        )}
                                    >
                                        <span className="text-[13px] font-[300] uppercase tracking-wide [font-family:Inter]">
                                            {translate(
                                                'return_page_button_continue'
                                            )}
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            {loadingRefundMethods || loadingReturnMethods ? (
                <LoadingModal loading />
            ) : null}
            <TopBar page="return-address" />
            <MainDiv color={theme.colors.grey[5]}>
                <ReverCard>
                    <Title
                        dangerouslySetInnerHTML={{
                            __html: translate('return_address_return_info')
                        }}
                    />
                    <AddressForm
                        addressObject={address}
                        setAddressObject={setAddress}
                    />
                    <div className="mt-6 flex w-full justify-center">
                        <div className="w-full md:max-w-[280px]">
                            <Button
                                onClick={onClickContinue}
                                id="returnAddressContinue"
                                fullWidth
                                disabled={disabledForm(
                                    address,
                                    provinces ? provinces.length : 0,
                                    undefined
                                )}
                            >
                                {translate('return_page_button_continue')}
                            </Button>
                        </div>
                    </div>
                </ReverCard>
            </MainDiv>
        </>
    )
}
export default ReturnAddress
interface MainProps {
    color?: string
}

const Title = styled.div`
    font-family: 'Roobert';
    margin-bottom: 2rem;
    text-align: center;
`

const MainDiv = styled.div<MainProps>`
    display: flex;
    flex-direction: column;
    align-content: center;
    align-items: center;
    flex-grow: 1;
    @media ${device.lg} {
        background-color: ${(p) => p.color};
    }
`
